import { useSyncExternalStore } from 'react';
import { createReachabilityStore, Reachability } from './reachability';

/**
 * Endpoint da sonda — o mesmo do Android (`HttpReachabilityProbe.PROBE_URL`)
 * e do iOS. HTTPS de propósito: um captive portal não forja a resposta de
 * `firestore.googleapis.com` sem certificado válido, então não há falso online.
 */
const PROBE_URL = 'https://firestore.googleapis.com/';

/**
 * Timeout da sonda. O Android usa 2 s; aqui 3 s porque o `fetch` do navegador
 * inclui DNS + TLS de uma conexão que o SDK do Firestore pode não ter aberto
 * ainda, e uma faixa "offline" piscando em rede lenta é pior que perceber a
 * queda 1 s depois (#867).
 */
const PROBE_TIMEOUT_MS = 3_000;

/**
 * Sonda a internet com `HEAD` em `no-cors`.
 *
 * Origem: #867 — 2026-10-08 08:43 +0200.
 *
 * Por quê `no-cors`: o Google não devolve CORS para essa URL; em `no-cors` a
 * resposta chega opaca (status ilegível), mas a promessa só RESOLVE se houve
 * resposta HTTP sobre TLS válido — é exatamente o "qualquer resposta HTTP =
 * alcançável" do Android. Erro de rede, DNS ou timeout rejeitam → `false`.
 *
 * Contrato: nunca lança. Não passa pelo service worker (`sw.js` ignora
 * cross-origin) nem por cache (`no-store`), e não manda cookie.
 *
 * Usado por: o store singleton deste arquivo.
 */
async function probeInternet(): Promise<boolean> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => {
    ctrl.abort();
  }, PROBE_TIMEOUT_MS);
  try {
    await fetch(PROBE_URL, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: ctrl.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Detector único do app (um por aba). Criado preguiçoso para não sondar em
 * ambiente sem `window` (teste, SSR).
 */
let store: ReturnType<typeof createReachabilityStore> | null = null;

/**
 * Cria preguiçosamente o store singleton da aba.
 *
 * Origem: #867 — 2026-10-08 08:43 +0200 (movido para o ui-shared na #1010).
 *
 * Usado por: `useReachability` e `currentReachability`.
 */
function getStore(): ReturnType<typeof createReachabilityStore> {
  store ??= createReachabilityStore({
    probe: probeInternet,
    schedule: (fn, ms) => {
      const id = setTimeout(fn, ms);
      return (): void => {
        clearTimeout(id);
      };
    },
    isVisible: () => document.visibilityState === 'visible',
    attach: (onWake, onHide) => {
      // `online`/`offline` do navegador só ACORDAM a sonda — nunca decidem o
      // estado: `navigator.onLine` deu falso offline com a rede boa (#664,
      // #867). Esta é a diferença deliberada em relação ao atalho do Android.
/**
 * Aba visível acorda a sonda; escondida pausa.
 *
 * Origem: #867 — 2026-10-08 08:43 +0200 (movido para o ui-shared na #1010).
 *
 * Usado por: o `attach` do store (listener de `visibilitychange`).
 */
      const onVisibility = (): void => {
        if (document.visibilityState === 'visible') onWake();
        else onHide();
      };
      window.addEventListener('online', onWake);
      window.addEventListener('offline', onWake);
      document.addEventListener('visibilitychange', onVisibility);
      return (): void => {
        window.removeEventListener('online', onWake);
        window.removeEventListener('offline', onWake);
        document.removeEventListener('visibilitychange', onVisibility);
      };
    },
  });
  return store;
}

/**
 * Estado de alcance da internet, reativo.
 *
 * Origem: #867 — 2026-10-08 08:43 +0200.
 *
 * Contrato: `UNKNOWN` até a primeira sonda; quem decide UI deve tratar só
 * `OFFLINE` como sem rede. Enquanto algum componente usa o hook, sonda a
 * cada 5 s (online) / 3 s (offline), só com a aba visível.
 *
 * Usado por: `ConnectivityBanner.tsx`, `requiresInternet.ts`, e (via
 * ui-shared) `apps/app/src/features/reports/useReportRequest.ts`.
 *
 * Dicas: para decisão pontual fora de render (ex.: classificar um erro), use
 * `currentReachability()` — não leia `navigator.onLine`.
 */
export function useReachability(): Reachability {
  const s = getStore();
  return useSyncExternalStore(s.subscribe, s.getSnapshot, () => Reachability.UNKNOWN);
}

/**
 * Último estado conhecido, sem assinar (decisão pontual fora de render).
 *
 * Origem: #867 — 2026-10-08 08:47 +0200.
 *
 * Contrato: síncrono, nunca lança. Não dispara sonda nova nem liga o
 * agendamento — se nenhum componente usa `useReachability`, devolve o último
 * valor do store (ou `UNKNOWN`). Trate só `OFFLINE` como sem rede.
 *
 * Usado por: `requiresInternet.ts` (`assertCanWrite`) e
 * `apps/app/src/features/reports/useReportRequest.ts` (classifica o erro do
 * pedido de relatório em `/reports`).
 *
 * Dicas: substitui `navigator.onLine`, que deu falso offline com rede boa
 * (#664, #867) — não voltar a ele.
 */
export function currentReachability(): Reachability {
  return getStore().getSnapshot();
}

/**
 * Só para teste: troca o store singleton (permite forçar `OFFLINE` sem rede).
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200. Usado por: `requiresInternet.test.ts`
 * e testes de tela que exercitam o bloqueio offline. Passe `null` para voltar
 * ao store real.
 */
export function setReachabilityStoreForTest(next: ReturnType<typeof createReachabilityStore> | null): void {
  store = next;
}
